import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SquareArrowOutUpRight } from "lucide-react";
import { SearchInput } from "../../components/ui/search-input.component";
import { cn } from "@lab/src/lib/util";
import { Logger } from "@/lib/logger/Logger";
import { supabase } from "@/lib/supabase";
import {
	JobWithShare,
	PatientEntityType,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { usePatientStore } from "../../store/Patient";
import { useJobStore } from "../../store/Jobs";
import {
	SheetContentType,
	useSheetStore,
} from "@/dentlab/src/store/Sheets/sheet.store";
import { Button } from "../../components/ui/button";
import { Toggle } from "../../components/ui/toggle";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";
import { TooltipWrapper } from "../../components/ui/tooltip-wrapper";
import { Loading } from "@/components/src/animations/loading";

const MAX_ROWS = 500;

type TraceabilityPatient = {
	id: string;
	code: string;
	first_name: string | null;
	last_name: string | null;
	title: string | null;
};

type JobItemTraceabilityRow = {
	id: string;
	code_e: string;
	lot: string | null;
	quantity: number;
	job_documents: {
		id: number;
		date: string | null;
		job_id: number;
		jobs: {
			id: number;
			code: string | null;
			title: string | null;
			client_id: string | null;
			patients: TraceabilityPatient | null;
		};
	};
};

/**
 * Lists every job an article was used in, with document date, patient,
 * LOT number and quantity — the audit trail required for material
 * traceability (MDR). Keyed by the article code (job_items.code_e);
 * without a code it searches across all articles of the organization.
 * Rendered inside the material-traceability sheet.
 */
export const MaterialTraceability: React.FC<{
	articleCode?: string;
}> = ({ articleCode }) => {
	const navigate = useNavigate();
	const { patientName, getPatient } = usePatientStore((state) => ({
		patientName: state.patientName,
		getPatient: state.getPatient,
	}));
	const { openSheet } = useSheetStore((state) => ({
		openSheet: state.openSheet,
	}));
	const { shareFile, unshareFile, acceptJobRequest, denyJobRequest } =
		useJobStore((state) => ({
			shareFile: state.shareFile,
			unshareFile: state.unshareFile,
			acceptJobRequest: state.acceptJobRequest,
			denyJobRequest: state.denyJobRequest,
		}));
	const [rows, setRows] = useState<JobItemTraceabilityRow[]>([]);
	const [totalCount, setTotalCount] = useState<number | null>(null);
	const [loading, setLoading] = useState(true);
	const [lotSearch, setLotSearch] = useState("");
	const [lotTerm, setLotTerm] = useState("");
	const [caseSensitive, setCaseSensitive] = useState(false);
	const lotDebounceRef = useRef<number>();
	const searchRef = useRef<HTMLInputElement>(null);

	// Focus the LOT input when the sheet opens — delayed past the Radix
	// focus scope, which would otherwise grab focus for the sheet chrome.
	useEffect(() => {
		const timer = window.setTimeout(() => searchRef.current?.focus(), 100);
		return () => window.clearTimeout(timer);
	}, []);

	const handleLotSearchChange = (value: string) => {
		setLotSearch(value);
		window.clearTimeout(lotDebounceRef.current);
		lotDebounceRef.current = window.setTimeout(
			() => setLotTerm(value.trim()),
			300
		);
	};

	useEffect(() => {
		let cancelled = false;
		async function fetchUsages() {
			// The global search across all articles only queries once a
			// LOT term was entered — never fetch the whole usage history.
			if (!articleCode && !lotTerm) {
				setRows([]);
				setTotalCount(null);
				setLoading(false);
				return;
			}
			setLoading(true);
			let query = supabase
				.from(SupabaseTableEnum.JOB_ITEMS)
				.select(
					"id, code_e, lot, quantity, job_documents!inner(id, date, job_id, jobs!inner(id, code, title, client_id, patients(id, code, first_name, last_name, title)))",
					{ count: "exact" }
				)
				// code_e is a shared namespace with tariffs — only article rows
				.neq("type", JobItemTypeEnum.TARIFF)
				.is("job_documents.deleted_at", null);
			if (articleCode) {
				query = query.eq("code_e", articleCode);
			}
			if (lotTerm) {
				const escaped = lotTerm.replace(/[\\%_]/g, (c) => `\\${c}`);
				const pattern = `%${escaped}%`;
				query = caseSensitive
					? query.like("lot", pattern)
					: query.ilike("lot", pattern);
			}
			const { data, error, count } = await query
				.order("job_documents(date)", { ascending: false })
				.limit(MAX_ROWS);
			if (cancelled) return;
			if (error || !data) {
				Logger.error(error);
				setLoading(false);
				return;
			}
			setRows(data as unknown as JobItemTraceabilityRow[]);
			setTotalCount(count);
			setLoading(false);
		}
		fetchUsages();
		return () => {
			cancelled = true;
		};
	}, [articleCode, lotTerm, caseSensitive]);

	const handleOpenJob = (row: JobItemTraceabilityRow) => {
		const job = row.job_documents.jobs;
		if (!job.client_id) return;
		navigate(`/client/${job.client_id}/job/${job.id}`);
	};

	const handleOpenJobSheet = (row: JobItemTraceabilityRow) => {
		const job = row.job_documents.jobs;
		openSheet(SheetContentType.JOB, {
			job: {
				id: job.id,
				code: job.code,
				title: job.title,
				client_id: job.client_id,
				patient_code: job.patients?.code,
			} as Partial<JobWithShare>,
			onShareFile: shareFile,
			onUnshareFile: unshareFile,
			onAcceptJobRequest: acceptJobRequest,
			onDenyJobRequest: denyJobRequest,
		});
	};

	const handleOpenPatient = async (patientId: string) => {
		const patient = await getPatient(patientId);
		if (patient) {
			openSheet(SheetContentType.PATIENT, { patient });
		}
	};

	const formatDate = (date: string | null) =>
		date ? new Date(date).toLocaleDateString("de-CH") : "–";

	const content = (
		<>
			<div className="flex items-center gap-2">
				<div
					className="w-full max-w-xs"
					data-testid="traceability-lot-search"
				>
					<SearchInput
						ref={searchRef}
						value={lotSearch}
						onChange={handleLotSearchChange}
						placeholder="Nach LOT-Nummer filtern..."
					/>
				</div>
				<TooltipWrapper
					title={
						caseSensitive
							? "Groß-/Kleinschreibung wird beachtet"
							: "Groß-/Kleinschreibung wird ignoriert"
					}
				>
					<Toggle
						variant="outline"
						pressed={caseSensitive}
						onPressedChange={setCaseSensitive}
						className={cn(
							caseSensitive &&
								"border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
						)}
						aria-label="Groß-/Kleinschreibung beachten"
						data-testid="traceability-case-toggle"
					>
						Aa
					</Toggle>
				</TooltipWrapper>
			</div>
			{loading ? (
				<Loading height="80px" />
			) : rows.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					{lotTerm
						? "Keine Verwendungen mit dieser LOT-Nummer gefunden."
						: articleCode
							? "Dieser Artikel wurde noch in keinem Auftrag verwendet."
							: "Geben Sie eine LOT-Nummer ein, um Verwendungen über alle Artikel zu suchen."}
				</p>
			) : (
				<div className="min-h-0 overflow-auto">
					<Table>
						<TableHeader className="sticky top-0 z-10 bg-background">
							<TableRow>
								<TableHead className="w-[48px]">
									<span className="sr-only">Aktionen</span>
								</TableHead>
								{!articleCode && <TableHead>Artikel</TableHead>}
								<TableHead>Auftrag</TableHead>
								<TableHead className="w-[100px]">
									Datum
								</TableHead>
								<TableHead>Patient</TableHead>
								<TableHead>LOT</TableHead>
								<TableHead className="text-right">
									Anzahl
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.map((row) => {
								const job = row.job_documents.jobs;
								const patient = job.patients;
								return (
									<TableRow
										key={row.id}
										className="cursor-pointer"
										onClick={() => handleOpenJobSheet(row)}
									>
										<TableCell className="py-1">
											{job.client_id && (
												<TooltipWrapper title="Zum Auftrag">
													<Button
														variant="ghost"
														size="sm"
														className="h-7 w-7 p-0"
														onClick={(e) => {
															e.stopPropagation();
															handleOpenJob(row);
														}}
														aria-label={`Auftrag ${
															job.code ?? ""
														} öffnen`}
														data-testid="traceability-open-job"
													>
														<SquareArrowOutUpRight className="h-4 w-4" />
													</Button>
												</TooltipWrapper>
											)}
										</TableCell>
										{!articleCode && (
											<TableCell className="font-mono">
												{row.code_e}
											</TableCell>
										)}
										<TableCell>
											<div className="flex flex-col">
												<span>{job.code ?? "–"}</span>
												{job.title && (
													<span className="text-xs text-muted-foreground">
														{job.title}
													</span>
												)}
											</div>
										</TableCell>
										<TableCell>
											{formatDate(row.job_documents.date)}
										</TableCell>
										<TableCell>
											{patient ? (
												<button
													type="button"
													className="text-left underline-offset-4 hover:underline"
													aria-label="Patient anzeigen"
													data-testid="traceability-open-patient"
													onClick={(e) => {
														e.stopPropagation();
														handleOpenPatient(
															patient.id
														);
													}}
												>
													{patientName(
														patient as PatientEntityType
													)}
												</button>
											) : (
												"–"
											)}
										</TableCell>
										<TableCell>{row.lot || "–"}</TableCell>
										<TableCell className="text-right">
											{row.quantity}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</div>
			)}
			{totalCount !== null && totalCount > rows.length && (
				<p className="text-xs text-muted-foreground">
					Zeige die neuesten {rows.length} von {totalCount}{" "}
					Verwendungen.
				</p>
			)}
		</>
	);

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-5">
			{content}
		</div>
	);
};
